import * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid } from "@mui/x-data-grid";
import { useNotification } from "../../context/notification-provider";
import { useDialogs } from "../dialog/use-dialog";
import { listFilterOptionsByType, updateFilterOption } from "./service";
import { getColumns } from "./filter-options-data-grid";
import FilterOptionCreateDialog from "./filter-option-create-dialog";
import FilterOptionEditDialog from "./filter-option-edit-dialog";
import FilterOptionDeleteDialog from "./filter-option-delete-dialog";
import { FilterOptionDetail, FilterTypeDetail } from "./types";

const FilterOptionsPanel = ({
  filterType,
  onFilterTypeMutate,
}: {
  filterType: FilterTypeDetail;
  onFilterTypeMutate: () => void;
}) => {
  const { showNotification } = useNotification();
  const { confirm } = useDialogs();

  const [options, setOptions] = React.useState<FilterOptionDetail[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadingError, setLoadingError] = React.useState<unknown>(null);

  const [dialog, setDialog] = React.useState<"create" | "edit" | "delete" | "">(
    "",
  );
  const [selectedOption, setSelectedOption] =
    React.useState<FilterOptionDetail | null>(null);

  const loadOptions = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const updated = await listFilterOptionsByType(filterType.id);
      setOptions(updated);
      setLoadingError(null);
    } catch (err) {
      setLoadingError(err);
    } finally {
      setIsLoading(false);
    }
  }, [filterType.id]);

  React.useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const refetch = React.useCallback(async () => {
    await loadOptions();
    onFilterTypeMutate();
  }, [loadOptions, onFilterTypeMutate]);

  const handleCreateClick = React.useCallback(() => {
    setDialog("create");
  }, []);

  const handleOnEdit = React.useCallback((row: FilterOptionDetail) => {
    setSelectedOption(row);
    setDialog("edit");
  }, []);

  const handleOnDelete = React.useCallback((row: FilterOptionDetail) => {
    setSelectedOption(row);
    setDialog("delete");
  }, []);

  const handleOnDialogClose = React.useCallback(() => {
    setDialog("");
    setSelectedOption(null);
    refetch();
  }, [refetch]);

  const handleToggleEnabled = React.useCallback(
    async (option: FilterOptionDetail) => {
      const newValue = !option.isEnabled;
      const action = newValue ? "enable" : "disable";

      try {
        const result = await confirm(
          `Are you sure you want to ${action} the filter option "${option.description}"?`,
          {
            title: "Confirm Filter Option Status Change",
            okText: "Confirm",
            cancelText: "Cancel",
            severity: "warning",
          },
        );

        if (result === true) {
          await updateFilterOption({
            id: option.id,
            description: option.description,
            isEnabled: newValue,
            isDefaultSelected: option.isDefaultSelected,
            sortOrder: option.sortOrder,
            meta: option.meta,
          });
          showNotification({
            message: `Filter option "${option.description}" ${action}d.`,
            severity: "success",
            duration: 3000,
          });
          refetch();
        }
      } catch (toggleError) {
        showNotification({
          message: `Failed to update filter option. ${(toggleError as Error).message}`,
          severity: "error",
          duration: 3000,
        });
      }
    },
    [confirm, showNotification, refetch],
  );

  return (
    <Paper elevation={1} sx={{ mt: 2, p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Options for &quot;{filterType.label}&quot;
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={handleCreateClick}
          startIcon={<AddIcon />}
        >
          Add Option
        </Button>
      </Box>
      {loadingError ? (
        <Alert severity="error">Unable to load filter options.</Alert>
      ) : (
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={options}
            columns={getColumns({
              onEdit: handleOnEdit,
              onDelete: handleOnDelete,
              onToggleEnabled: handleToggleEnabled,
            })}
            disableRowSelectionOnClick
            loading={isLoading}
            density="compact"
            slotProps={{
              baseIconButton: {
                size: "small",
              },
            }}
          />
        </Box>
      )}
      {dialog === "create" && (
        <FilterOptionCreateDialog
          filterTypeId={filterType.id}
          open={dialog === "create"}
          onClose={handleOnDialogClose}
        />
      )}
      {dialog === "edit" && selectedOption && (
        <FilterOptionEditDialog
          selectedFilterOption={selectedOption}
          open={dialog === "edit"}
          onClose={handleOnDialogClose}
        />
      )}
      {dialog === "delete" && selectedOption && (
        <FilterOptionDeleteDialog
          selectedFilterOption={selectedOption}
          open={dialog === "delete"}
          onClose={handleOnDialogClose}
        />
      )}
    </Paper>
  );
};

export default FilterOptionsPanel;
